import React, {type PropsWithChildren, useCallback} from 'react';

import {useIntl} from 'react-intl';

import classNames from 'classnames';

import {type ModalData} from 'types/actions';
import Constants, {ModalIdentifiers} from 'utils/constants';

import MoreIcon from '../widgets/icons/more_icon';
import OverlayTrigger from '../overlay_trigger';
import Menu from 'components/widgets/menu/menu';
import MenuWrapper from 'components/widgets/menu/menu_wrapper';
import IframeModal from 'components/iframe_modal';
import Tooltip from '../tooltip';
import MenuGroup from '../widgets/menu/menu_group';

import styles from './textbox_more_button.module.css';

interface Props {
    locale: string;
    shouldShowPreview: boolean;
    onPreviewExit?: () => void;
    pluginItems?: React.ReactNode[];
    openUp?: boolean;
    newEditorEnabled?: boolean;
    onToggle?: (open: boolean) => void;
    actions: {
        openModal: <P>(modalData: ModalData<P>) => void;
        setShowPreview?: (showPreview: boolean) => void;
    };
}

export const TextboxMoreButton: React.FC<PropsWithChildren<Props>> = ({
    locale,
    shouldShowPreview,
    onPreviewExit,
    onToggle,
    openUp = true,
    actions,
    newEditorEnabled,
    children,
    pluginItems = [],
}) => {
    const {setShowPreview, openModal} = actions;

    const togglePreview = useCallback(() => {
        setShowPreview?.(!shouldShowPreview);
        if (shouldShowPreview && onPreviewExit) {
            setTimeout(onPreviewExit);
        }
    }, [setShowPreview, shouldShowPreview, onPreviewExit]);

    const showHelp = useCallback(() => {
        openModal({
            modalId: ModalIdentifiers.IFRAME,
            dialogType: IframeModal,
            dialogProps: {
                src: `/help/formatting?locale=${locale}`,
            },
        });
    }, [openModal, locale]);

    const {formatMessage} = useIntl();

    if (shouldShowPreview && !newEditorEnabled) {
        const editButtonTooltip = (
            <Tooltip id='editButtonTooltip'>
                <div aria-hidden={true}>{formatMessage({id: 'post_info.edit', defaultMessage: 'Edit'})}</div>
            </Tooltip>
        );
        return (
            <OverlayTrigger
                delayShow={Constants.OVERLAY_TIME_DELAY}
                placement='top'
                overlay={editButtonTooltip}
            >
                <button
                    type='button'
                    aria-label={''}
                    onClick={togglePreview}
                    className='post-action exit-preview-mode'
                >
                    <i
                        data-testid='draftIcon'
                        className='icon icon-pencil-outline'
                    />
                </button>
            </OverlayTrigger>
        );
    }

    const moreButtonTooltip = (
        <Tooltip id='helpButtonTooltip'>
            <div aria-hidden={true}>{formatMessage({id: 'post_info.dot_menu.tooltip.more_actions', defaultMessage: 'More'})}</div>
        </Tooltip>
    );

    const formattingMenuList = (
        <li className='MenuHelp'>
            <div>
                <b>
                    {formatMessage({id: 'textbox.bold', defaultMessage: '**bold**'})}
                </b>
                <br />
                <i>
                    {formatMessage({id: 'textbox.italic', defaultMessage: '*italic*'})}
                </i>
                <br />
                <span>
                    {'~~'}
                    <s>
                        {formatMessage({id: 'textbox.strike', defaultMessage: 'strike'})}
                    </s>
                    {'~~ '}
                </span>
                <br />
                <span>
                    {formatMessage({id: 'textbox.inlinecode', defaultMessage: '`inline code`'})}
                </span>
                <br />
                <span>
                    {formatMessage({id: 'textbox.preformatted', defaultMessage: '```preformatted```'})}
                </span>
                <br />
                <span>
                    {formatMessage({id: 'textbox.quote', defaultMessage: '> quote'})}
                </span>
            </div>
        </li>
    );

    const formatting = (
        <Menu.ItemSubMenu
            styleSelectableItem={true}
            root={true}
            text={formatMessage({id: 'textbox.formatting', defaultMessage: 'Formatting'})}
            extraBottomOffset={60}
            id='textbox-formatting'
            icon={<i className='icon-format-letter-case' />}
            subMenu={[
                {
                    id: 'formatting-submenu',
                    text: formattingMenuList,
                    hoverDisabled: true,
                },
                {
                    id: 'divider',
                    isDivider: true,
                },
                {
                    id: 'show-help',
                    text: formatMessage({id: 'textbox.help', defaultMessage: 'Help'}),
                    action: showHelp,
                },
            ]}
        />
    );

    const preview = (
        <Menu.ItemAction
            text={formatMessage({id: 'textbox.preview', defaultMessage: 'Preview'})}
            onClick={togglePreview}
            icon={<i className='icon-eye-outline' />}
        />
    );

    if (!pluginItems.length && newEditorEnabled) {
        return children;
    }

    return (
        <MenuWrapper onToggle={onToggle} placement='top right'>
            {children || (
                <OverlayTrigger
                    className='hidden-xs'
                    delayShow={Constants.OVERLAY_TIME_DELAY}
                    placement='top'
                    overlay={moreButtonTooltip}
                    rootClose={true}
                >
                    <button
                        id='textbox-more-buttom'
                        aria-label={formatMessage({
                            id: 'post_info.dot_menu.tooltip.more_actions',
                            defaultMessage: 'More',
                        })}
                        className='post-action help-button'
                        aria-expanded='false'
                        type='button'
                    >
                        <MoreIcon size={12} />
                    </button>
                </OverlayTrigger>
            )}
            <Menu
                className={classNames({[styles.menu]: newEditorEnabled})}
                id='textbox-more-menu'
                ariaLabel={formatMessage({id: 'post_info.menuAriaLabel', defaultMessage: 'Post extra options'})}
                openUp={openUp}
                alwaysOpenUp={openUp}
                openLeft={true}
                right={0}
            >
                {pluginItems.length > 0 && (
                    <MenuGroup>
                        {pluginItems}
                    </MenuGroup>
                )}
                {!newEditorEnabled && (
                    <MenuGroup>
                        {formatting}
                        {preview}
                    </MenuGroup>
                )}
            </Menu>
        </MenuWrapper>
    );
};
